// @flow
import React from 'react';
import styled from 'styled-components';
import { connect } from 'react-redux';
import bindAll from 'lodash/bindAll';
import { withRouter } from 'react-router-dom';


import * as actions from '../../modules/actions';
import colors from '../../assets/css/colors';

import JoinLeagueSettingsPopup from '../../pages/leagues/leaguesJoin/JoinLeagueSettingsPopup.js';
import {
	LeagueAvatar,
	LeagueName,
	ConfirmationModal,
	LeagueTypeLabel,
	BorderedIcon,
} from '..';
import { StyledPageTitle } from '../../pages/leagues/MyLeagues';
import ArrowRight from '../Icons/ArrowRight';
import Settings from '../Icons/Settings';

const Wrapper = styled.section`
	width: 100%;
`;

const Title = styled.h2`
	font-size: 15px;
	line-height: 1.666;
	color: ${colors.darkGrey};
	margin: 1.25em 0;
	font-weight: bold;
`;

const League = styled.div`
	width: 100%;
	box-sizing: border-box;
	padding: 10px 25px 10px 30px;
	border: 1px dashed #CAD2D8;
	min-height: 60px;
	margin-bottom: 20px;
	display: flex;
	flex-direction: row;
	justify-content: space-between;
	align-items: center;
	position: relative;
`;

const StyledLeagueTypeLabel = styled(LeagueTypeLabel)`
	position: absolute;
	top: 0;
	left: 0;
`;

const LeagueNameWrapper = styled.div`
	display: flex;
	flex-direction: row;
	justify-content: flex-start;
	align-items: center;
	> :first-child {
		width: 40px;
		height: 40px;
		margin-right: 10px;
	}
`;

const Details = styled.div`
	display: flex;
	flex-direction: row;
	font-weight: 400;
	text-transform: uppercase;
	color: ${colors.title};
	> div {
		margin: 0 5px;
		text-align: center;
		font-size: 10px;
		line-height: 1.5;
		width: 11.5em;
	}
	span {
		color: ${colors.title};
		display: block;
		font-weight: 700;
		font-size: 14px;
		line-height: 1.14;
		margin-top: 0.34em;
	}
`;

const Actions = styled.div`
	display: flex;
	flex-direction: row;
	justify-content: flex-end;
	justify-content: center;
	> * {
		margin: 0 5px;
	}
`;


type Props = {
	leagues: [{
		id: number,
		name: string,
		firstname: string,
		lastname: string,
		avatar_version: number,
		avatar_prefix: string,
		is_club?: boolean,
		users: Array<Object>,
		size: number,
		privacy: 1 | 0,
		start_round: number,
	}],
	fetchRegenerateLeaguesList: typeof actions.leagueDraft.regenerateListDraftLeagues,
	fetchRegenerateLeague: typeof actions.leagueDraft.regenerateShowDraftLeague,
	regenerate_list: Object,
	history: {
		push: Function
	},
	large_heading?: boolean
}

type State = {
	confirm_modal: boolean,
	selected_regenerated_league_id: number,
	redirect_league_id: number,
	settings_modal: boolean
}

class RegenerateLeaguesComp extends React.Component<Props, State> {
	constructor(props) {
		super(props);
		bindAll(this, ['handleRegnerateLeague']);
	}

	state = {
		confirm_modal: false,
		selected_regenerated_league_id: 0,
		redirect_league_id: 0,
		settings_modal: false
	}

	componentDidMount() {
		this.props.fetchRegenerateLeaguesList();
	}

	componentDidUpdate(prev_props:Props) {
		// fetch each regen league
		if(prev_props.regenerate_list.length === 0 && this.props.regenerate_list.length > 0) {
			this.props.regenerate_list.forEach(v => {
				this.props.fetchRegenerateLeague({ id: v.id });
			});
		}
	}

	handleRegnerateLeague() {
		this.props.history
			 .push(`/leagues/create/${this.state.selected_regenerated_league_id}`);
		this.setState({ confirm_modal: false, settings_modal: false });
	}

	render () {
		const { leagues, large_heading } = this.props;
		const {
			confirm_modal,
			settings_modal,
			selected_regenerated_league_id
		} = this.state;

		if(leagues.length === 0) {
			return null;
		}
		const CURRENT_YEAR = process.env.REACT_APP_CURRENT_YEAR ;
		const title = `Regenerate your ${parseInt(CURRENT_YEAR,10) -1} leagues`;
		return (
			<Wrapper>
				{confirm_modal && <ConfirmationModal
					body_text="Are you sure you want to regenerate this league?"
					header_text="Regenerate League"
					cancelClick={() => this.setState({ confirm_modal: false })}
					closeClick={() => this.setState({ confirm_modal: false })}
					confirmClick={this.handleRegnerateLeague}
				/>}
				{settings_modal &&
					<JoinLeagueSettingsPopup
						join={() => this.setState({ settings_modal: false, confirm_modal: true })}
						onClick={() => this.setState({ settings_modal: false })}
						// $FlowFixMe flow is too dumb to work out the find will return an object
						league={leagues.find(l => l.id === selected_regenerated_league_id)}
						is_regenerated_league
					/>
				}
				{large_heading? (
					<StyledPageTitle>
						{title}
					</StyledPageTitle>
				) : (
					<Title>
						{title}
					</Title>
				)}

				{leagues.map(league => {
					return (
						<League key={league.id}>
							<StyledLeagueTypeLabel rotate="-90"
								text={league.privacy ? 'PUBLIC' : 'PRIVATE'}
							/>
							<LeagueNameWrapper>
								<div>
									<LeagueAvatar league={league} size="40px" />
								</div>
								<LeagueName
									name={league.name}
									width="11.4em"
									commissioner="You are the Commissioner"
								/>
							</LeagueNameWrapper>
							<Details>
								<div>
									<p>TEAMS</p>
									<span>
										{league.size}
									</span>
								</div>
							</Details>
							<Actions>
								<BorderedIcon hover={colors.lightGreen}>
									<Settings cursor="pointer"
										size="1.875"
										color={colors.nrlDarkGreen}
										onClick={() => {
											this.setState({
												settings_modal: true,
												selected_regenerated_league_id: league.id
											});
										}}
									/>
								</BorderedIcon>
								<BorderedIcon hover={colors.lightGreen}>
									<ArrowRight cursor="pointer"
										size="1.875"
										color={colors.nrlDarkGreen}
										onClick={() => {
											this.setState({
												confirm_modal: true,
												selected_regenerated_league_id: league.id
											});
										}}
									/>
								</BorderedIcon>
							</Actions>
						</League>
					);
				})}
			</Wrapper>
		);
	}
}

const mapStateToProps = state => ({
	regenerate_list: state.leagues.regenerate_list.result,
	leagues: state.leagues.regenerate_list.result
		.filter(l => l.id in state.leagues.regenerate_show.by_id)
		.map(l => state.leagues.regenerate_show.by_id[l.id])
});

const mapDispatchToProps = {
	fetchRegenerateLeaguesList: actions.leagueDraft.regenerateListDraftLeagues,
	fetchRegenerateLeague: actions.leagueDraft.regenerateShowDraftLeague,
};


export const RegenerateLeagues =
	withRouter(connect(mapStateToProps, mapDispatchToProps)(RegenerateLeaguesComp));
export default RegenerateLeagues;


// WEBPACK FOOTER //
// ./src/components/RegenerateLeagues/index.js